//
// # WebSocket Reconnection Manager
//
// Shared utility for managing WebSocket reconnection with exponential backoff.
//

export interface ReconnectionConfig {
  // Base delay in milliseconds for first reconnection attempt (default: 2000)
  baseDelayMs?: number;
  // Maximum delay in milliseconds (default: 60000)
  maxDelayMs?: number;
  // Connection duration in milliseconds after which to reset attempts (default: 60000)
  stableConnectionThresholdMs?: number;
}

const DEFAULT_CONFIG: Required<ReconnectionConfig> = {
  baseDelayMs: 2000,
  maxDelayMs: 60000,
  stableConnectionThresholdMs: 60000,
};

/**
 * Manages WebSocket reconnection state with exponential backoff.
 *
 * Features:
 * - Exponential backoff: delay doubles each attempt (2s, 4s, 8s, ...)
 * - Maximum delay cap: prevents excessively long waits
 * - Stable connection detection: resets attempts after being connected for threshold duration
 *
 * Usage:
 * ```typescript
 * const manager = new ReconnectionManager();
 *
 * // On connection established:
 * manager.recordConnectionStart();
 *
 * // On connection closed:
 * manager.recordConnectionEnd(); // Returns true if connection was stable
 * const delay = manager.getNextDelay();
 * setTimeout(reconnect, delay);
 * manager.recordAttempt();
 *
 * // On successful reconnection:
 * manager.reset();
 * ```
 */
export class ReconnectionManager {
  private readonly config: Required<ReconnectionConfig>;
  private attempts: number = 0;
  private connectionStartTime: number = 0;

  constructor(config: ReconnectionConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  /**
   * Calculate the delay for the next reconnection attempt.
   * Uses exponential backoff: baseDelay * 2^attempts, capped at maxDelay.
   */
  getNextDelay(): number {
    return Math.min(
      this.config.baseDelayMs * Math.pow(2, this.attempts),
      this.config.maxDelayMs
    );
  }

  /**
   * Get the current number of reconnection attempts.
   */
  getAttempts(): number {
    return this.attempts;
  }

  /**
   * Record that a reconnection attempt is being made.
   * Call this after scheduling the reconnection.
   */
  recordAttempt(): void {
    this.attempts++;
  }

  /**
   * Record that a connection has been established.
   * Call this when the WebSocket opens.
   */
  recordConnectionStart(): void {
    this.connectionStartTime = Date.now();
  }

  /**
   * Record that a connection has ended and check if it was stable.
   * If the connection lasted longer than the threshold, resets the attempt counter.
   *
   * @returns Object with connection duration info and whether attempts were reset
   */
  recordConnectionEnd(): {
    durationMs: number;
    wasStable: boolean;
    attemptsReset: boolean;
  } {
    const durationMs =
      this.connectionStartTime > 0 ? Date.now() - this.connectionStartTime : 0;
    const wasStable = durationMs >= this.config.stableConnectionThresholdMs;
    const attemptsReset = wasStable;

    if (attemptsReset) {
      this.attempts = 0;
    }

    this.connectionStartTime = 0;
    return { durationMs, wasStable, attemptsReset };
  }

  /**
   * Reset the reconnection state.
   * Call this when a connection is successfully established.
   */
  reset(): void {
    this.attempts = 0;
    this.connectionStartTime = Date.now();
  }

  /**
   * Format duration in milliseconds as a human-readable string.
   */
  static formatDuration(durationMs: number): string {
    if (durationMs <= 0) {
      return "unknown";
    }
    return (durationMs / 1000).toFixed(1) + "s";
  }
}
